import { Fragment, type ReactNode, useSyncExternalStore } from 'react';
import type { Entry } from './locales/en';

/**
 * Idioma del dashboard (las dos marcas): español, inglés, polaco, italiano, portugués o griego.
 *
 * - Automático, por la ubicación del navegador, como la landing: la API dice desde qué país se
 *   conecta (CloudFront → CloudFront-Viewer-Country, GET /api/admin/auth/locale) y el país elige el
 *   idioma (Polonia → polaco, Italia → italiano, Grecia y Chipre → griego, Portugal y Brasil →
 *   portugués, España y Latinoamérica → español; el resto, inglés). Sin país (en local, o si la
 *   API no responde), el idioma del navegador; si no es ninguno de los seis, inglés. El país se
 *   recuerda en este navegador (localStorage "dash-lang-auto") para no esperar a la API la próxima vez.
 * - Manual: el selector del menú y de la página de entrada. La elección se guarda en este navegador
 *   (localStorage "dash-lang") y manda sobre la automática.
 *
 * Los textos se escriben en español en el código y se traducen al mostrarlos: el español es la clave
 * de cada diccionario (src/locales/<idioma>.ts, uno por idioma, que se baja sólo si hace falta). Si
 * una clave no está, se ve el español (nunca la clave cruda: es el mismo texto).
 *
 *   t('Página {page} de {pages}', { page, pages })
 *   tn(n, '{n} moto', '{n} motos')                       singular o plural según n
 *   tr('¿Eliminar {name}?', { name: <strong>…</strong> })  con elementos de React dentro
 *   tk('Nuevo')                                          marca un texto de una constante; se traduce con t() al mostrarlo
 *
 * En los componentes se usa useT(): da las mismas funciones y vuelve a pintar el componente cuando
 * cambia el idioma. Las funciones sueltas (t, tn) son para el código que no es de React (la API, las subidas).
 * Revisar los diccionarios: node --experimental-strip-types scripts/check-locales.mjs
 */

export type Lang = 'es' | 'en' | 'pl' | 'it' | 'pt' | 'el';
/** Valores de las {variables} de un texto. */
export type Vars = Record<string, string | number>;
/** Igual, pero pueden ser elementos de React. */
export type RichVars = Record<string, ReactNode>;

/** Los idiomas del selector, cada uno con su nombre en su propio idioma. */
export const LANGUAGES: readonly { code: Lang; label: string; name: string }[] = [
  { code: 'es', label: 'ES', name: 'Español' },
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'pl', label: 'PL', name: 'Polski' },
  { code: 'it', label: 'IT', name: 'Italiano' },
  { code: 'pt', label: 'PT', name: 'Português' },
  { code: 'el', label: 'EL', name: 'Ελληνικά' },
];

/** Locale de Intl de cada idioma (fechas, "hace 5 minutos", plurales). */
export const LOCALES: Record<Lang, string> = { es: 'es', en: 'en-GB', pl: 'pl', it: 'it', pt: 'pt-PT', el: 'el' };

/**
 * El idioma de cada país (código ISO en minúsculas): el mismo reparto que la landing de Phelon & Moore
 * (src/data/geo.json de phelon-moore-web), más Brasil y los países africanos de lengua portuguesa,
 * que en el dashboard sí tienen su idioma. Los demás países, inglés.
 */
const COUNTRY_LANG: Record<string, Lang> = {
  pl: 'pl',
  it: 'it', sm: 'it', va: 'it',
  pt: 'pt', br: 'pt', ao: 'pt', mz: 'pt', cv: 'pt',
  gr: 'el', cy: 'el',
  ...Object.fromEntries(['es', 'ad', 'mx', 'gt', 'sv', 'hn', 'ni', 'cr', 'pa', 'cu', 'do', 'pr', 'co', 've', 'ec', 'pe', 'bo', 'cl', 'ar', 'py', 'uy', 'gq'].map((c) => [c, 'es' as Lang])),
};
export const countryLang = (country: string | null | undefined): Lang | null =>
  country && /^[a-z]{2}$/i.test(country) ? (COUNTRY_LANG[country.toLowerCase()] ?? 'en') : null;

const STORAGE_KEY = 'dash-lang';
const AUTO_KEY = 'dash-lang-auto';

const CODES = LANGUAGES.map((l) => l.code);
const isLang = (v: unknown): v is Lang => typeof v === 'string' && (CODES as string[]).includes(v);

const read = (key: string): Lang | null => {
  try {
    const v = window.localStorage.getItem(key);
    return isLang(v) ? v : null;
  } catch {
    return null;
  }
};
const write = (key: string, v: Lang | null) => {
  try {
    if (v) window.localStorage.setItem(key, v);
    else window.localStorage.removeItem(key);
  } catch {
    /* sin almacenamiento: vale hasta recargar la página */
  }
};

/** La elección manual guardada (null si no hay o si el navegador no deja leer el almacenamiento). */
const storedLang = () => read(STORAGE_KEY);

/** El idioma del navegador: el primero de su lista que sea uno de los seis; si ninguno, inglés. */
export function browserLang(): Lang {
  try {
    const list = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const l of list) {
      const base = typeof l === 'string' ? l.toLowerCase().split('-')[0] : '';
      if (isLang(base)) return base;
    }
  } catch {
    /* sin navigator */
  }
  return 'en';
}

/** El idioma automático: el del país (si ya se supo), si no el del navegador. */
const autoLang = (): Lang => read(AUTO_KEY) ?? browserLang();

// ------------------------------------------------------------------ diccionarios (uno por idioma, bajo demanda)
type Dict = Record<string, Entry>;
const LOADERS: Record<Exclude<Lang, 'es'>, () => Promise<{ default: Dict }>> = {
  en: () => import('./locales/en'),
  pl: () => import('./locales/pl'),
  it: () => import('./locales/it'),
  pt: () => import('./locales/pt'),
  el: () => import('./locales/el'),
};
const dicts: Partial<Record<Lang, Dict>> = {};
const loading: Partial<Record<Lang, Promise<boolean>>> = {};
/** Baja el diccionario de `lang` (el español no tiene: es el del código). false si no se pudo. */
function loadDict(lang: Lang): Promise<boolean> {
  if (lang === 'es' || dicts[lang]) return Promise.resolve(true);
  return (loading[lang] ??= LOADERS[lang]()
    .then((m) => {
      dicts[lang] = m.default;
      return true;
    })
    .catch(() => {
      delete loading[lang];   // se puede reintentar (p. ej. al volver la conexión)
      return false;
    }));
}

// ------------------------------------------------------------------ estado (uno solo, fuera de React)
let current: Lang = 'es';
let wanted: Lang = 'es';
const listeners = new Set<() => void>();

/** Pasa a `lang` en cuanto su diccionario está (mientras tanto sigue el anterior: nunca se ve a medias). */
async function apply(lang: Lang): Promise<void> {
  wanted = lang;
  const ok = await loadDict(lang);
  if (wanted !== lang) return;            // mientras bajaba se pidió otro
  const next = ok ? lang : 'es';
  try {
    document.documentElement.lang = next;
  } catch {
    /* sin DOM */
  }
  if (next !== current) {
    current = next;
    for (const l of listeners) l();
  }
}

/** El idioma actual (para el código que no es de React). */
export const getLang = (): Lang => current;

/** Elección manual (el selector): se recuerda en este navegador y manda sobre la automática. */
export function setLang(lang: Lang): void {
  write(STORAGE_KEY, lang);
  void apply(lang);
}

/**
 * El país desde el que se conecta el navegador, según la API (CloudFront). Recuerda el idioma que le
 * corresponde y, si no hay elección manual, pasa a él. Devuelve el idioma, o null si no se supo.
 */
async function detectRegion(): Promise<Lang | null> {
  try {
    const res = await fetch('/api/admin/auth/locale', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const body = (await res.json()) as { country?: string | null };
    const lang = countryLang(body.country);
    if (!lang) return null;
    write(AUTO_KEY, lang);
    if (!storedLang()) await apply(lang);
    return lang;
  } catch {
    return null;
  }
}

/**
 * Arranque (main.tsx lo espera antes de pintar): el idioma elegido a mano, o el del país, o el del
 * navegador. Deja bajado su diccionario. El país se pregunta a la API en cada entrada (por si cambió),
 * pero sólo se espera la primera vez, cuando todavía no se sabe (con un límite, para no demorar la
 * entrada; si llega tarde, cambia al llegar).
 */
export async function initLang(): Promise<void> {
  const manual = storedLang();
  const known = manual ?? read(AUTO_KEY);
  const first = apply(known ?? browserLang());
  if (manual) return first;
  const region = detectRegion();
  if (known) return first;
  await Promise.race([Promise.all([first, region]), new Promise((ok) => setTimeout(ok, 900))]);
}

try {
  // Se eligió en otra pestaña (o se borró la elección): se sigue aquí también.
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY || e.key === null) void apply(storedLang() ?? autoLang());
  });
  // Cambió el idioma del navegador: solo importa si no se eligió a mano ni se sabe el país.
  window.addEventListener('languagechange', () => {
    if (!storedLang() && !read(AUTO_KEY)) void apply(browserLang());
  });
} catch {
  /* sin window */
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** El idioma actual; el componente se vuelve a pintar cuando cambia. */
export const useLang = (): Lang => useSyncExternalStore(subscribe, getLang, getLang);

// ------------------------------------------------------------------ traducción
const warned = new Set<string>();
const pluralRules = perLangRaw((locale) => new Intl.PluralRules(locale));

/** El texto de `key` en `lang` (sin las variables). Sin traducción, el español. */
function lookup(lang: Lang, key: string, n?: unknown): string {
  if (lang === 'es') return key;
  const entry = dicts[lang]?.[key];
  if (entry === undefined) {
    if (import.meta.env.DEV && dicts[lang] && !warned.has(lang + key)) {
      warned.add(lang + key);
      console.warn(`[i18n] falta la traducción (${lang}) de ${JSON.stringify(key)}`);
    }
    return key;
  }
  if (typeof entry === 'string') return entry;
  const form = typeof n === 'number' ? pluralRules(lang).select(n) : 'other';
  return entry[form] ?? entry.other;
}

/** Reemplaza las {variables}; las que no vienen quedan como están. */
const fill = (text: string, vars?: Vars) => (vars ? text.replace(/\{(\w+)\}/g, (m, k: string) => (vars[k] !== undefined ? String(vars[k]) : m)) : text);

export interface Translator {
  lang: Lang;
  /** Traduce un texto, con sus {variables}. */
  t: (key: string, vars?: Vars) => string;
  /**
   * Singular o plural según `n` (que llega al texto como {n}). En inglés, una clave del diccionario
   * puede traer las dos formas aunque en español sea una sola ("{n} PDF").
   */
  tn: (n: number, one: string, other: string, vars?: Vars) => string;
  /** Como t(), pero las variables pueden ser elementos de React (un <strong>, un enlace…). */
  tr: (key: string, vars: RichVars) => ReactNode;
}

function translator(lang: Lang): Translator {
  const t = (key: string, vars?: Vars) => fill(lookup(lang, key, vars?.n), vars);
  return {
    lang,
    t,
    tn: (n, one, other, vars) => t(n === 1 ? one : other, { ...vars, n }),
    tr: (key, vars) =>
      lookup(lang, key)
        .split(/\{(\w+)\}/)
        .map((part, i) => (i % 2 === 0 ? part : <Fragment key={i}>{vars[part] !== undefined ? vars[part] : `{${part}}`}</Fragment>)),
  };
}

const translators = Object.fromEntries(CODES.map((l) => [l, translator(l)])) as Record<Lang, Translator>;

/** Las funciones de traducción del idioma actual; el componente se vuelve a pintar cuando cambia. */
export const useT = (): Translator => translators[useLang()];

/** t() fuera de React (mensajes de error de la API, de las subidas…): usa el idioma de ese momento. */
export const t: Translator['t'] = (key, vars) => translators[current].t(key, vars);
export const tn: Translator['tn'] = (n, one, other, vars) => translators[current].tn(n, one, other, vars);

/** Marca un texto de una constante para traducirlo después con t() (no lo cambia). */
export const tk = <S extends string>(key: S): S => key;

// ------------------------------------------------------------------ formatos
/** Un objeto de Intl por idioma, creado la primera vez que hace falta. */
function perLangRaw<T>(make: (locale: string) => T): (lang: Lang) => T {
  const cache: Partial<Record<Lang, T>> = {};
  return (lang) => (cache[lang] ??= make(LOCALES[lang]));
}
export function perLang<T>(make: (locale: string) => T): (lang?: Lang) => T {
  const get = perLangRaw(make);
  return (lang = current) => get(lang);
}

const enOrdinal = new Intl.PluralRules('en-GB', { type: 'ordinal' });
const EN_ORDINAL: Record<string, string> = { one: 'st', two: 'nd', few: 'rd', other: 'th' };

/** 1ª, 2ª… · 1st, 2nd… · 1. · 1.ª · 1η (las marchas de la transmisión). */
export const ordinal = (n: number, lang: Lang = current): string => {
  switch (lang) {
    case 'en':
      return `${n}${EN_ORDINAL[enOrdinal.select(n)] ?? 'th'}`;
    case 'pl':
      return `${n}.`;
    case 'pt':
      return `${n}.ª`;
    case 'el':
      return `${n}η`;
    default:
      return `${n}ª`;   // es, it
  }
};
